import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Ruler, Home, Calendar, Layers, CheckCircle2, Share2, Copy, MessageCircle, X, Wrench, Compass, BedDouble, Building2, User, Palette, Tag } from "lucide-react";
import api from "../lib/api";
import { alignClass } from "../lib/align";
import { useSettings, waLink } from "../context/SettingsContext";
import { Button, SmartImage, Reveal } from "../components/ui";
import ProjectCard from "../components/ProjectCard";
import Lightbox from "../components/Lightbox";
import LeadForm from "../components/LeadForm";
import BeforeAfter from "../components/BeforeAfter";
import SEO from "../components/SEO";

export default function ProjectDetail() {
  const { slug } = useParams();
  const { settings } = useSettings();
  const [project, setProject] = useState(null);
  const [lb, setLb] = useState({ items: [], index: null });
  const [modal, setModal] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setProject(null);
    api.get(`/projects/${slug}`).then((r) => setProject(r.data)).catch(() => setProject(false));
    window.scrollTo(0, 0);
  }, [slug]);

  if (project === false) return <div className="pt-40 pb-24 text-center container-x"><h1 className="text-2xl font-bold">Project not found</h1><Button to="/projects" className="mt-6">Back to Projects</Button></div>;
  if (!project) return <div className="pt-40 pb-24 text-center text-muted">Loading...</div>;

  const images = project.images || [];
  const before = images.find((i) => i.category === "Before");
  const after = images.find((i) => i.category === "After");
  const galleryItems = images.map((im) => ({ url: im.url, name: project.name, category: im.category }));

  const fv = project.field_visibility || {};
  const vis = (key) => fv[key] !== false; // visible unless explicitly turned off
  const areaText = project.area ? `${project.area}${project.area_unit ? " " + project.area_unit : ""}` : "";
  const specDefs = [
    ["location", "Location", project.location, MapPin],
    ["area", "Area", areaText, Ruler],
    ["plot_size", "Plot Size", project.plot_size, Home],
    ["built_up_area", "Built-up Area", project.built_up_area, Building2],
    ["floors", "Floors", project.floors, Layers],
    ["facing", "Facing", project.facing, Compass],
    ["bhk", "BHK", project.bhk, BedDouble],
    ["service", "Service", project.service, Wrench],
    ["category", "Category", project.category, Layers],
    ["status", "Status", project.status, CheckCircle2],
    ["completion_year", "Year", project.completion_year, Calendar],
    ["architecture_style", "Style", project.architecture_style, Palette],
    ["client_name", "Client", project.client_name, User],
  ];
  const meta = specDefs.filter(([key, , val]) => val && vis(key)).map(([, label, val, Icon]) => [label, val, Icon]);
  const customSpecs = (project.custom_specs || []).filter((s) => s && s.label && s.value);

  const waMsg = `Hello ${settings.company_name || "GNONS Design & Construction"}, I am interested in a project similar to ${project.name}.`;
  const shareUrl = window.location.href;

  const share = async () => {
    if (navigator.share) { try { await navigator.share({ title: project.name, text: project.description, url: shareUrl }); } catch (e) {} }
    else { navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 1800); }
  };

  return (
    <>
      <SEO title={project.seo_title || project.name} description={project.seo_description || project.description} image={project.cover_image} type="article" />

      <section className="pt-24 md:pt-28 bg-sand">
        <div className="container-x grid lg:grid-cols-3 gap-8 py-8">
          <div className="lg:col-span-2">
            <div className="relative rounded-xl2 overflow-hidden shadow-lift border border-line bg-beige">
              <button onClick={() => setLb({ items: galleryItems, index: 0 })} className="block w-full" data-testid="project-cover-btn"><SmartImage src={project.cover_image} alt={project.name} natural eager /></button>
            </div>
          </div>
          <div className="lg:col-span-1">
            <div className="kicker mb-2">{project.category} Project</div>
            <h1 className="text-3xl md:text-4xl font-extrabold text-ink">{project.name}</h1>
            <div className="mt-6 space-y-3">
              {meta.map(([label, val, Icon]) => (
                <div key={label} className="flex items-center gap-3 text-sm">
                  <Icon size={17} className="text-gold shrink-0" />
                  <span className="text-muted w-24">{label}:</span>
                  <span className="font-semibold text-ink">{val}</span>
                </div>
              ))}
              {customSpecs.map((s, i) => (
                <div key={`c${i}`} className="flex items-center gap-3 text-sm" data-testid={`custom-spec-${i}`}>
                  <Tag size={17} className="text-gold shrink-0" />
                  <span className="text-muted w-24">{s.label}:</span>
                  <span className="font-semibold text-ink">{s.value}</span>
                </div>
              ))}
            </div>
            <div className="mt-7 flex flex-col gap-3">
              <button onClick={() => setModal(true)} data-testid="project-inquiry-btn" className="inline-flex items-center justify-center gap-2 rounded-full bg-gold text-white font-semibold py-3.5 shadow-gold hover:bg-gold-dark transition-colors">
                <Wrench size={17} /> Get Similar Design
              </button>
              <a href={waLink(settings.whatsapp, waMsg)} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 rounded-full border border-line py-3.5 font-semibold text-ink hover:border-gold hover:text-gold transition-colors">
                <MessageCircle size={17} /> Enquire on WhatsApp
              </a>
            </div>
            <div className="mt-5 flex items-center gap-3">
              <span className="text-sm text-muted">Share:</span>
              <a href={waLink(settings.whatsapp, `${project.name} — ${shareUrl}`)} target="_blank" rel="noreferrer" aria-label="Share on WhatsApp" className="w-9 h-9 rounded-full border border-line flex items-center justify-center hover:text-gold hover:border-gold"><MessageCircle size={16} /></a>
              <button onClick={share} aria-label="Share" className="w-9 h-9 rounded-full border border-line flex items-center justify-center hover:text-gold hover:border-gold"><Share2 size={16} /></button>
              <button onClick={() => { navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 1800); }} aria-label="Copy link" className="w-9 h-9 rounded-full border border-line flex items-center justify-center hover:text-gold hover:border-gold"><Copy size={16} /></button>
              {copied && <span className="text-xs text-gold">Link copied!</span>}
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-white">
        <div className="container-x max-w-4xl space-y-10">
          <Reveal>
            <h2 className="text-2xl font-bold text-ink mb-3">Project Overview</h2>
            <p className={`text-muted leading-relaxed ${alignClass(project.overview_align)}`}>{project.overview || project.description}</p>
          </Reveal>
          {project.design_concept && (
            <Reveal>
              <h2 className="text-2xl font-bold text-ink mb-3">Design Concept</h2>
              <p className={`text-muted leading-relaxed ${alignClass(project.design_concept_align)}`}>{project.design_concept}</p>
            </Reveal>
          )}

          {before && after && (
            <Reveal>
              <h2 className="text-2xl font-bold text-ink mb-4">Before / After</h2>
              <BeforeAfter before={before.url} after={after.url} />
            </Reveal>
          )}
        </div>
      </section>

      {images.length > 0 && (
        <section className="pb-16 md:pb-24 bg-white">
          <div className="container-x">
            <h2 className="text-2xl font-bold text-ink mb-8">Gallery</h2>
            <div className="columns-2 md:columns-3 gap-3 md:gap-4">
              {images.map((im, i) => (
                <button key={i} onClick={() => setLb({ items: galleryItems, index: i })} data-testid={`gallery-img-${i}`} className="group relative mb-3 md:mb-4 block w-full break-inside-avoid rounded-xl overflow-hidden border border-line bg-beige">
                  <SmartImage src={im.url} alt={im.alt || project.name} natural imgClass="group-hover:scale-[1.03] transition-transform duration-700" />
                  {im.category && <span className="absolute bottom-2 left-2 text-[10px] font-bold uppercase tracking-wide bg-ink/70 text-white px-2 py-1 rounded">{im.category}</span>}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {project.related?.length > 0 && (
        <section className="py-16 bg-sand">
          <div className="container-x">
            <h2 className="text-2xl font-bold text-ink mb-8">Related Projects</h2>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-5">
              {project.related.map((p) => <ProjectCard key={p.id} project={p} onOpen={() => {}} aspect="aspect-[4/5]" />)}
            </div>
          </div>
        </section>
      )}

      <Lightbox items={lb.items} index={lb.index} onClose={() => setLb({ items: [], index: null })} onIndex={(i) => setLb((s) => ({ ...s, index: i }))} />

      <AnimatePresence>
        {modal && (
          <motion.div className="fixed inset-0 z-[90] bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setModal(false)}>
            <motion.div className="bg-white rounded-xl2 w-full max-w-lg p-6 md:p-8 max-h-[90vh] overflow-y-auto" initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0 }} onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-start mb-4">
                <div><div className="kicker mb-1">Get Similar Design</div><h3 className="text-xl font-bold text-ink">Project Like "{project.name}"</h3></div>
                <button onClick={() => setModal(false)} className="w-9 h-9 rounded-full border border-line flex items-center justify-center"><X size={18} /></button>
              </div>
              <LeadForm sourcePage="Get Similar Design" sourceProject={project.name} extra={{ source_project_category: project.category, source_project_image: project.cover_image }} onDone={() => setModal(false)} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
